import { useGT } from 'gt-react';
import { formatCurrency } from '@erp/shared-utils';
import type { LineTotals } from '../utils/lineTotals';
import { formatLbs } from '../utils/lineWeight';

/**
 * The detail table's footer: totals for the lines in the cart. A small row of
 * its own because DataTable has no footer support; it sits under the table and
 * says what it counts, since the table above also lists lines left out.
 */
export function LineTotalsRow({ totals }: { totals: LineTotals }) {
  const gt = useGT();
  return (
    <div
      role="group"
      aria-label={gt('Totals for approved lines', {
        context: 'detail table totals row label',
      })}
      className="flex items-center justify-end gap-4 border-t px-2 py-1 text-xs tabular-nums"
    >
      <span className="font-medium">
        {gt('Approved', { context: 'detail table totals row label' })}
      </span>
      <span>
        <span className="text-muted-foreground">
          {gt('Lines', { context: 'detail table totals row, line count' })}
        </span>{' '}
        {totals.lineCount.toLocaleString()}
      </span>
      <span>
        <span className="text-muted-foreground">
          {gt('Qty', { context: 'detail table totals row, total quantity' })}
        </span>{' '}
        {totals.totalQuantity.toLocaleString()}
      </span>
      <span>
        <span className="text-muted-foreground">
          {gt('Cost', { context: 'detail table totals row, extended cost' })}
        </span>{' '}
        {formatCurrency(totals.totalCost)}
      </span>
      <span
        className="text-muted-foreground"
        title={
          totals.unweighedLineCount > 0
            ? gt(
                '{count, plural, =1 {# line has no weight on file and is not counted} other {# lines have no weight on file and are not counted}}',
                {
                  context: 'detail table totals row, weight tooltip',
                  count: totals.unweighedLineCount,
                },
              )
            : undefined
        }
      >
        {gt('Wt', { context: 'detail table totals row, total weight' })}{' '}
        {totals.totalWeightLbs == null ? '—' : formatLbs(totals.totalWeightLbs)}
      </span>
    </div>
  );
}
